import { MetricCard } from "@/modules/shared/components/metric-card";
import { Skeleton } from "@/ui/components/primitives/skeleton";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";

interface KPIData {
  todayPayin: {
    amount: number;
    count: number;
    change: number;
  };
  todayPayout: {
    amount: number;
    count: number;
    change: number;
  };
  successRate: {
    percentage: number;
    dailyChange: number;
  };
}

interface MerchantKPIProps {
  kpiData: KPIData | null;
  totalVolume: number;
  isLoading?: boolean;
}

// Helper function to format volume values
function formatVolume(amount: number): string {
  if (!amount || amount === 0) return "₹0";

  if (amount >= 10000000) {
    // For values in crores (10M)
    return `₹${(amount / 10000000).toFixed(1)}Cr`;
  } else if (amount >= 100000) {
    // For values in lakhs (100K)
    return `₹${(amount / 100000).toFixed(1)}L`;
  } else if (amount >= 1000) {
    // For values in thousands
    return `₹${(amount / 1000).toFixed(1)}K`;
  } else {
    // For smaller values
    return `₹${amount.toLocaleString()}`;
  }
}

export function MerchantKPICards({ kpiData, totalVolume, isLoading }: MerchantKPIProps) {
  if (isLoading || !kpiData) {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-6 border rounded-lg space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-3 w-20" />
          </div>
        ))}
      </div>
    );
  }

  // Ensure all values have defaults
  const todayPayin = kpiData?.todayPayin || { amount: 0, change: 0 };
  const todayPayout = kpiData?.todayPayout || { amount: 0, change: 0 };
  const successRate = kpiData?.successRate || { percentage: 0, dailyChange: 0 };
  const volume = totalVolume || 0;

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard
        title="Today's Payin"
        value={`₹${(todayPayin.amount || 0).toLocaleString()}`}
        change={`${todayPayin.change >= 0 ? '+' : ''}${todayPayin.change || 0}% from yesterday`}
        changeType={todayPayin.change >= 0 ? "positive" : "negative"}
        icon={TrendingUp}
        iconColor="text-success"
      />
      <MetricCard
        title="Today's Payout"
        value={`₹${(todayPayout.amount || 0).toLocaleString()}`}
        change={`${todayPayout.change >= 0 ? '+' : ''}${todayPayout.change || 0}% from yesterday`}
        changeType={todayPayout.change >= 0 ? "positive" : "negative"}
        icon={TrendingDown}
        iconColor="text-destructive"
      />
      <MetricCard
        title="Success Rate"
        value={`${(successRate.percentage || 0).toFixed(1)}%`}
        change={`${successRate.dailyChange >= 0 ? '+' : ''}${(successRate.dailyChange || 0).toFixed(1)}% today`}
        changeType={successRate.dailyChange >= 0 ? "positive" : "negative"}
        icon={Activity}
        iconColor="text-primary"
      />
      <MetricCard
        title="Total Volume"
        value={formatVolume(volume)}
        change="Today"
        changeType="neutral"
        icon={DollarSign}
        iconColor="text-warning"
      />
    </div>
  );
}